import {
  ref,
  Ref,
  onMounted,
  onUnmounted,
  reactive,
  UnwrapRef,
  ComponentInternalInstance,
  getCurrentInstance,
  ComputedRef,
  computed,
} from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
interface DrawerData {
  list: SMH.SensitiveWordData[];
  total: string;
  drawerLoading: boolean;
}
type Handler = {
  tableData: Ref<any>; // SMH.SensitiveDataParams
  loading: Ref<boolean>;
  methods: {
    initData: () => void;
    onDelete: (word: string) => void;
    getSensitiveList: (word: string) => void;
  };
  columns: Array<any>;
  showListDrawer: Ref<boolean>;
  drawerData: UnwrapRef<DrawerData>;
  chooseWord: Ref<string>;
};
export default function initList(): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const store = vm.$store;
  const tableData = ref([]);
  const columns = [
    {
      colKey: 'word',
      cell: 'word',
      title: i18n_t('敏感词'),
      align: 'center',
      width: 180,
    },
    {
      colKey: 'hitCount',
      cell: 'hitCount',
      title: i18n_t('触发次数'),
      align: 'center',
      width: 130,
    },
    {
      colKey: 'trustCount',
      cell: 'trustCount',
      title: i18n_t('信任次数'),
      align: 'center',
      width: 130,
    },
    {
      colKey: 'handler',
      title: i18n_t('操作'),
      align: 'center',
      width: 100,
    },
  ];
  let timer: number | undefined;
  const loading = ref(false);
  const showListDrawer = ref(false);
  const drawerData = reactive<DrawerData>({
    total: '0',
    list: [],
    drawerLoading: false,
  });
  const chooseWord = ref('');
  const methods = {
    async pullList(isInit = false) {
      try {
        loading.value = isInit;
        // 获取敏感词的统计列表
        const { data } = await smh.userAPI.getSensitiveCountList();
        tableData.value = data.map((item: any) => {
          item.scanProgress = Math.floor(item?.scanProgress * 100) | 0;
          return item;
        });
        loading.value = false;
      } catch (err: MsgError) {
        tips.error(err);
        loading.value = false;
      }
    },
    // 删除的操作
    async onDelete(word: string) {
      loading.value = true;
      try {
        tips.loading(i18n_t('正在删除'));
        await smh.userAPI.deleteSensitiveWord({
          words: [word],
        });
        reporter.report('sensitive_manage', {
          del_status: 'true',
        });
        tips.success(i18n_t('删除成功'));
        methods.pullList(true);
      } catch (error: MsgError) {
        reporter.report('sensitive_manage', {
          del_status: 'false',
        });
        tips.error(error);
        loading.value = false;
      }
    },
    initData() {
      methods.pullList(true);
    },
    handlePath(spaceTag: string, path: Array<string>, name?: string) {
      let result = '';
      let temp = '';
      const teamName = name && name?.length > 9 ? `${name?.slice(0, 7)}...` : name;
      const hasSlash = path.length > 0 ? '/' : '';
      if (spaceTag === 'personal') {
        temp = i18n_t('个人空间{hasSlash}', {
          hasSlash,
        });
      } else if (spaceTag === 'team') {
        temp = i18n_t('企业空间/{teamName}{hasSlash}', {
          teamName,
          hasSlash,
        });
      } else if (spaceTag === 'group') {
        temp = i18n_t('协作空间/{teamName}{hasSlash}', {
          teamName,
          hasSlash,
        });
      }
      const pathLength = path.join('/').length;
      // 当路径的字符长度大于十六的时候需要做截断处理
      if (pathLength > 16) {
        if (path.slice(-1).length > 9) {
          const slicePath = `${path.slice(-1)[0].slice(0, 7)}...`;
          result = `${temp}...${slicePath}`;
        } else {
          result = `${temp}.../${path.slice(-1)[0]}`;
        }
      } else {
        result = `${temp}${path.join('/')}`;
      }
      return result;
    },
    async getSensitiveList(word: string) {
      showListDrawer.value = true;
      drawerData.drawerLoading = true;
      chooseWord.value = word;
      try {
        // 获取敏感词的统计列表
        const { data } = await smh.userAPI.getSensitiveWordList({
          word,
        });
        drawerData.total = data.totalNum;
        drawerData.list = data.contents.map(item => {
          if (!item.path) {
            item.originPath = i18n_t('未知路径');
          } else if (item.spaceTag === 'personal') {
            item.originPath = methods.handlePath(item.spaceTag, item.path.slice(0, -1));
          } else if (item.spaceTag === 'team') {
            item.originPath = methods.handlePath(item.spaceTag, item.path.slice(0, -1), item.team?.name);
          } else if (item.spaceTag === 'group') {
            item.originPath = methods.handlePath(item.spaceTag, item.path.slice(0, -1), item.group?.name);
          }
          return item;
        });
        drawerData.drawerLoading = false;
      } catch (err: MsgError) {
        tips.error(err);
        drawerData.drawerLoading = false;
      }
    },
    async mountedCheckData() {
      await store.dispatch('GetOrgInfo', store.getters.orgId);
      // 若无敏感词功能，不进行接口调用
      if (store.getters.enableSensitiveWord) {
        methods.initData();
        // 为了获取扫描的进度
        timer = window.setInterval(() => {
          methods.pullList();
        }, 10000);
      }
    },
  };
  onMounted(() => methods.mountedCheckData());
  onUnmounted(() => {
    window.clearInterval(timer);
    timer = undefined;
  });
  return {
    tableData,
    methods,
    loading,
    columns,
    showListDrawer,
    drawerData,
    chooseWord,
  };
}
